import { useEffect, useState } from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import MuiDialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

import { withTranslation } from "react-i18next";

// 합계 판정 오차 허용치 — old PopKpiWeightConfig의 Math.abs(total - 100) < 0.01과 동일.
const TOTAL_EPSILON = 0.01;
const TOTAL_TARGET = 100;

/**
 * KPI 가중치 설정 팝업 — old `view/common/PopKpiWeightConfig.jsx` 대응.
 * AI 추천(가중합 점수)의 지표별 가중치를 **%(0~100)** 단위로 입력받아 0~1 스케일로 돌려준다.
 *
 * old는 react-hook-form + 공용 InputField였지만 new는 화면 state + MUI 직접 사용
 * (fp-simulation-screen-guide.md §B-5와 동일 규칙). 검증 실패 스낵바를 팝업이 직접 띄우지 않고
 * 호출부에 올리는 것도 §B-10 관례다 — 스낵바는 화면이 하나만 갖는다.
 *
 * props:
 *  - open
 *  - kpiWeights: [{ field, label, unitLabel?, weight(0~1), higherIsBetter }]  — 현재 값
 *    `unitLabel`은 라벨이 겹치는 항목을 구분하는 단위 키다(작업 교체 시간/횟수) — "라벨 (단위)"로 합성.
 *  - initialWeights: { [field]: 0~1 }                              — 초기화(REFRESH) 기준값
 *  - onClose()
 *  - onSubmit(weights|null, validationMessage?)  — weights는 { [field]: 0~1 }
 */
const KpiWeightConfigPopup = ({ t, open, kpiWeights = [], initialWeights, onClose, onSubmit }) => {
  const [values, setValues] = useState({});

  // 입력값은 문자열로 들고 있는다 — 지우는 도중("" 상태)에도 0으로 튀지 않게.
  const toFormValues = (source) =>
    Object.fromEntries(kpiWeights.map(kw => [kw.field, String(Math.round((source?.[kw.field] ?? 0) * 100))]));

  useEffect(() => {
    if (!open) return;
    setValues(toFormValues(Object.fromEntries(kpiWeights.map(kw => [kw.field, kw.weight]))));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const total = kpiWeights.reduce((sum, kw) => sum + (parseFloat(values[kw.field]) || 0), 0);
  const isTotalValid = Math.abs(total - TOTAL_TARGET) < TOTAL_EPSILON;

  const handleSubmit = () => {
    const hasEmpty = kpiWeights.some(kw => values[kw.field] === undefined || String(values[kw.field]).trim() === "");
    if (hasEmpty) {
      onSubmit(null, t("MSG_PleaseEnterValues", { values: t("AI_RECOMMENDATION_KPI_WEIGHT_CONFIG") }));
      return;
    }
    if (!isTotalValid) {
      onSubmit(null, t("MSG_WEIGHT_SUM_INVALID"));
      return;
    }
    onSubmit(Object.fromEntries(kpiWeights.map(kw => [kw.field, (parseFloat(values[kw.field]) || 0) / 100])));
  };

  return (
    <MuiDialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t("AI_RECOMMENDATION_KPI_WEIGHT_CONFIG")}</DialogTitle>
      <DialogContent dividers>
        <Typography sx={{ fontSize: 14, mb: 1.5, lineHeight: 1.6 }}>
          {t("AI_RECOMMENDATION_WEIGHT_DESC")}
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {kpiWeights.map(kw => (
            <TextField
              key={kw.field}
              size="small"
              type="number"
              label={kw.unitLabel ? `${t(kw.label)} (${t(kw.unitLabel)})` : t(kw.label)}
              value={values[kw.field] ?? ""}
              onChange={event => setValues(prev => ({ ...prev, [kw.field]: event.target.value }))}
              inputProps={{ min: 0, max: 100 }}
            />
          ))}
        </Box>
        <Box
          sx={{
            mt: 2, px: 1.75, py: 1, borderRadius: 1, border: 1,
            borderColor: isTotalValid ? "success.light" : "error.light",
            bgcolor: (theme) => alpha(theme.palette[isTotalValid ? "success" : "error"].main, 0.08),
            display: "flex", justifyContent: "space-between", alignItems: "center"
          }}
        >
          <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{t("SUM")}</Typography>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: isTotalValid ? "success.main" : "error.main" }}>
            {`${total.toFixed(1)} / ${TOTAL_TARGET}`}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button startIcon={<RestartAltIcon />} sx={{ mr: "auto" }} onClick={() => setValues(toFormValues(initialWeights))}>
          {t("reset")}
        </Button>
        <Button onClick={onClose}>{t("cancel")}</Button>
        <Button variant="contained" onClick={handleSubmit}>{t("ok")}</Button>
      </DialogActions>
    </MuiDialog>
  );
};

export default withTranslation()(KpiWeightConfigPopup);
